import * as React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Activity,
  CalendarCheck,
  Dna,
  FileText,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Settings,
  Sparkles,
  ThermometerSun,
  User,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { ErrorBoundary } from "@/components/error-boundary";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/cn";

const NAV_ITEMS: ReadonlyArray<{
  to: string;
  label: string;
  icon: React.ReactNode;
}> = [
  {
    to: "/",
    label: "Dashboard",
    icon: <LayoutDashboard className="h-4 w-4" />,
  },
  {
    to: "/symptoms",
    label: "Symptoms",
    icon: <ThermometerSun className="h-4 w-4" />,
  },
  {
    to: "/documents",
    label: "Documents",
    icon: <FileText className="h-4 w-4" />,
  },
  { to: "/profile", label: "Profile", icon: <User className="h-4 w-4" /> },
  { to: "/chat", label: "Chat", icon: <MessageSquare className="h-4 w-4" /> },
  {
    to: "/analysis",
    label: "Analysis",
    icon: <Sparkles className="h-4 w-4" />,
  },
  { to: "/protocol", label: "Protocol", icon: <Dna className="h-4 w-4" /> },
  {
    to: "/track",
    label: "Track",
    icon: <CalendarCheck className="h-4 w-4" />,
  },
  { to: "/account", label: "Account", icon: <Settings className="h-4 w-4" /> },
];

export const AppShell: React.FC = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    void navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <a href="#main" className="skip-link" data-testid="skip-link">
        Skip to main content
      </a>
      <header className="border-b bg-card print:hidden">
        <div className="container flex h-14 items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground"
              aria-hidden="true"
            >
              <Activity className="h-5 w-5" />
            </span>
            <span className="text-base font-semibold">Health Analyzer</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void handleSignOut();
            }}
            data-testid="nav-signout"
            aria-label="Sign out"
          >
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
            Sign out
          </Button>
        </div>
        <nav
          aria-label="Primary"
          className="container -mb-px flex flex-wrap gap-1 overflow-x-auto py-1 text-sm"
        >
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "inline-flex items-center gap-2 rounded-md px-3 py-2 transition-colors",
                  isActive
                    ? "bg-primary/10 font-semibold text-primary"
                    : "text-muted-foreground hover:bg-muted",
                )
              }
            >
              <span aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </header>
      <main
        id="main"
        tabIndex={-1}
        className="container py-6 focus:outline-none"
      >
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
};
